import { JobOfferPreview } from "@/lib/interfaces/embed";
import { Button } from "../../../ui/components/ui/button";
import { getIcon } from "../icon-map";
import { formatWorkingTimeModels, getReadableTextColor, shouldRenderJobPreviewImage, withAlpha } from "./v2-helpers";

/** (m/w/d) style abbreviations anywhere in the title, including partial e.g. (m / w). */
const GENDER_PAREN = /\s*\(\s*[mMdDwW](?:\s*(?:[/,|-]\s*|\s+)[mMdDwW])*\s*\)/g;

const STANDARD_GENDER_SUFFIX = " (m / w / d)";

function stripGenderParentheticals(title: string): string {
  return title.replace(GENDER_PAREN, "").replace(/\s+/g, " ").trim();
}

const getJobTitle = (jobTitle: string, color: string) => {
  const baseTitle = stripGenderParentheticals(jobTitle);
  return (
    <>
      {baseTitle}
      <span style={{ color }}>{STANDARD_GENDER_SUFFIX}</span>
    </>
  );
};

export function JobPostsV2Hero({
  brandColor,
  jobOfferPreview,
  heroEyebrowText,
  onApplyClick,
}: {
  brandColor: string;
  jobOfferPreview: JobOfferPreview;
  heroEyebrowText?: string;
  onApplyClick: () => void;
}) {
  const chipBg = withAlpha(brandColor, 0.2);
  const chipText = "#111827";

  const LocationIcon = getIcon("location");
  const EuroIcon = getIcon("euro");
  const ClockIcon = getIcon("clock");
  const SendIcon = getIcon("send");

  const workingTimeLabel = formatWorkingTimeModels(jobOfferPreview.workingTimeModels);
  const showImage = shouldRenderJobPreviewImage(jobOfferPreview);

  return (
    <section className="embed-job-hero w-full bg-gray-100">
      <div className="mx-auto max-w-6xl px-4 py-10 md:py-14">
        <div className={`grid grid-cols-1 ${showImage ? "lg:grid-cols-2" : ""} gap-10 items-center`}>
          <div className={`m-auto ${showImage ? "" : "text-center"}`}>
            {heroEyebrowText?.trim() ? (
              <div className="text-sm font-medium text-gray-600">{heroEyebrowText.trim()}</div>
            ) : null}
            <h1 className="mt-3 text-4xl md:text-5xl font-bold tracking-tight text-gray-900">
              {getJobTitle(jobOfferPreview.title, brandColor)}
            </h1>

            <div className={`mt-6 flex flex-wrap gap-3 ${showImage ? "" : "justify-center"}`}>
              {jobOfferPreview.location ? (
                <div
                  className="inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-medium"
                  style={{ backgroundColor: chipBg, color: chipText }}
                >
                  {LocationIcon ? (
                    <span className="[&>svg]:w-4 [&>svg]:h-4" style={{ color: brandColor }} aria-hidden="true">
                      <LocationIcon />
                    </span>
                  ) : null}
                  <span>{jobOfferPreview.location}</span>
                </div>
              ) : null}

              {jobOfferPreview.hourlySalary ? (
                <div
                  className="inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-medium"
                  style={{ backgroundColor: chipBg, color: chipText }}
                >
                  {EuroIcon ? (
                    <span className="[&>svg]:w-4 [&>svg]:h-4" style={{ color: brandColor }} aria-hidden="true">
                      <EuroIcon />
                    </span>
                  ) : null}
                  <span>{jobOfferPreview.hourlySalary}€ / Stunde</span>
                </div>
              ) : null}

              {workingTimeLabel ? (
                <div
                  className="inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-medium"
                  style={{ backgroundColor: chipBg, color: chipText }}
                >
                  {ClockIcon ? (
                    <span className="[&>svg]:w-4 [&>svg]:h-4" style={{ color: brandColor }} aria-hidden="true">
                      <ClockIcon />
                    </span>
                  ) : null}
                  <span>{workingTimeLabel}</span>
                </div>
              ) : null}
            </div>

            <div className="mt-7">
              <Button
                type="button"
                variant="ghost"
                className="embed-job-hero-cta rounded-full px-6 py-3 text-sm font-semibold shadow-none hover:opacity-90 hover:bg-transparent gap-2"
                style={{ backgroundColor: brandColor, color: getReadableTextColor(brandColor) }}
                onClick={onApplyClick}
              >
                {SendIcon ? (
                  <span className="[&>svg]:w-4 [&>svg]:h-4" aria-hidden="true">
                    <SendIcon />
                  </span>
                ) : null}
                Jetzt in 60 Sekunden bewerben
              </Button>
              <div className="mt-2 text-xs text-gray-500">
                Unverbindlich und vertraulich – wir melden uns persönlich per WhatsApp.
              </div>
            </div>
          </div>
          {showImage && (
            <div className="w-full">
              <div className="embed-job-hero-media relative overflow-hidden rounded-2xl">
                <img
                  src={jobOfferPreview.previewImageUrl}
                  alt=""
                  className="h-[320px] md:h-[360px] w-full object-contain"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
